import { Button, Typography, type ButtonProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import useTracking from 'app/tracking/useTracking'
import { ANALYTICS_OBJECTS } from 'app/utils/constants'

/**
 * Props for the CTAButton component
 * @interface CTAButtonProps
 * @property {string} buttonText - The text to display on the button
 * @property {() => void} onClick - Function to call when the button is clicked
 * @property {'primary' | 'outlined'} variant - The visual style variant of the button
 * @property {boolean} disabled - Whether the button is disabled
 * @property {Record<string, string>} trackingProps - Optional tracking data attributes for analytics pixels
 */
export interface CTAButtonProps extends Pick<ButtonProps, 'loading'> {
    buttonText: string
    onClick?: () => void
    variant: 'primary' | 'outlined'
    disabled?: boolean
    trackingProps?: Record<string, string>
    /** Defaults to `cta-button`; set when multiple CTAs exist on one page (e.g. confirm payment schedule). */
    buttonTestId?: string
}

/**
 * A customizable call-to-action button component
 * @component
 * @param {CTAButtonProps} props - The props for the CTAButton component
 * @returns {JSX.Element} The rendered CTAButton component
 */
export default function CTAButton({ buttonText, onClick, variant = 'primary', disabled, trackingProps, loading, buttonTestId = 'cta-button' }: CTAButtonProps) {
    const { trackClick } = useTracking()
    const theme = useTheme()
    const color = variant === 'primary' ? theme.color.white : theme.color.figGreen
    return (
        <Button
            type="button"
            data-testid={buttonTestId}
            variant={variant === 'primary' ? 'contained' : 'outlined'}
            sx={{
                backgroundColor: variant === 'primary' ? theme.color.figGreen : 'transparent',
                borderRadius: { xs: '80px', sm: '100px' },
                padding: '14px 24px',
                width: '100%',
                marginBottom: '16px',
                textTransform: 'none',
                border: variant === 'outlined' ? `1px solid ${theme.color.figGreen}` : 'none',
                boxShadow: 'none'
            }}
            onClick={() => {
                trackClick(`${ANALYTICS_OBJECTS.CTA_BUTTON}: ${buttonText}`)
                onClick?.()
            }}
            disabled={disabled}
            loading={loading}
            {...trackingProps}
        >
            <Typography variant="textButton" sx={{ color: loading ? 'transparent' : color }}>
                {buttonText}
            </Typography>
        </Button>
    )
}
